import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api, { customerAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import { FiEdit2, FiMapPin, FiPlus, FiTrash2 } from 'react-icons/fi';
import { COUNTRIES, getStatesForCountry, formatPhoneNumber } from '../../utils/addressData';

const emptyForm = {
  name: '',
  contact_name: '',
  contact_phone: '',
  street_address: '',
  address_line_2: '',
  city: '',
  state_province: '',
  postal_code: '',
  country: 'Canada',
  notes: '',
  is_default: false
};

const normalizePostalCode = (value) => String(value || '').toUpperCase();

const CustomerAddresses = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('source');
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const { data: profile, isLoading: profileLoading } = useQuery(
    ['customer-profile-for-addresses'],
    async () => {
      const response = await customerAPI.getProfile();
      return response.data || response;
    },
    { staleTime: 300000 }
  );

  const companyId = profile?.company?.id;

  const { data: sourceAddresses = [], isLoading: sourceLoading } = useQuery(
    ['customer-source-addresses', companyId],
    async () => {
      const response = await api.get('/customers/warehouses');
      return response.data?.data || [];
    },
    { enabled: !!companyId, staleTime: 300000 }
  );

  const { data: destinationAddresses = [], isLoading: destinationLoading } = useQuery(
    ['customer-destination-addresses', companyId],
    async () => {
      const response = await api.get(`/shipping/addresses/companies/${companyId}`);
      return response.data?.addresses || [];
    },
    { enabled: !!companyId, staleTime: 300000 }
  );

  const currentAddresses = useMemo(
    () => (activeTab === 'source' ? sourceAddresses : destinationAddresses),
    [activeTab, sourceAddresses, destinationAddresses]
  );

  const isLoading = profileLoading || sourceLoading || destinationLoading;
  const availableStates = getStatesForCountry(formData.country);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingAddress(null);
    setShowForm(false);
  };

  const invalidateAddressQueries = () => {
    queryClient.invalidateQueries(['customer-source-addresses', companyId]);
    queryClient.invalidateQueries(['customer-destination-addresses', companyId]);
  };

  const createMutation = useMutation(
    async (payload) => {
      if (activeTab === 'source') {
        const response = await api.post('/customers/warehouses', payload);
        return response.data;
      }

      const response = await api.post('/shipping/addresses', {
        ...payload,
        company_id: companyId,
        address_type: 'shipping'
      });
      return response.data;
    },
    {
      onSuccess: () => {
        invalidateAddressQueries();
        resetForm();
      }
    }
  );

  const updateMutation = useMutation(
    async ({ id, payload }) => {
      if (activeTab === 'source') {
        const response = await api.put(`/customers/warehouses/${id}`, payload);
        return response.data;
      }

      const response = await api.patch(`/shipping/addresses/${id}`, payload);
      return response.data;
    },
    {
      onSuccess: () => {
        invalidateAddressQueries();
        resetForm();
      }
    }
  );

  const deleteMutation = useMutation(
    async (id) => {
      if (activeTab === 'source') {
        const response = await api.delete(`/customers/warehouses/${id}`);
        return response.data;
      }

      const response = await api.delete(`/shipping/addresses/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        invalidateAddressQueries();
      }
    }
  );

  const handleInputChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : name === 'postal_code'
          ? normalizePostalCode(value)
          : name === 'contact_phone'
            ? formatPhoneNumber(value)
            : name === 'country'
              ? value
              : value
    }));
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      name: address.name || '',
      contact_name: address.contact_name || address.name || '',
      contact_phone: address.contact_phone || address.phone || '',
      street_address: address.street_address || '',
      address_line_2: address.address_line_2 || '',
      city: address.city || '',
      state_province: address.state_province || '',
      postal_code: normalizePostalCode(address.postal_code || ''),
      country: address.country || 'Canada',
      notes: address.notes || '',
      is_default: address.is_default || false
    });
    setShowForm(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const payload = {
      name: formData.name,
      contact_name: formData.contact_name,
      contact_phone: formData.contact_phone,
      street_address: formData.street_address,
      address_line_2: formData.address_line_2,
      city: formData.city,
      state_province: formData.state_province,
      postal_code: formData.postal_code,
      country: formData.country,
      notes: formData.notes,
      is_default: formData.is_default
    };

    if (editingAddress) {
      updateMutation.mutate({ id: editingAddress.id, payload });
      return;
    }

    createMutation.mutate(payload);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this address?')) return;
    deleteMutation.mutate(id);
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Addresses</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your source pickup addresses and outbound destination addresses.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditingAddress(null);
            setFormData(emptyForm);
            setShowForm(true);
          }}
          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <FiPlus className="mr-2 h-4 w-4" />
          Add {activeTab === 'source' ? 'Source' : 'Destination'} Address
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => {
              setActiveTab('source');
              resetForm();
            }}
            className={`rounded-md px-4 py-3 text-sm font-medium ${
              activeTab === 'source' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Source Addresses
            <div className="mt-1 text-xs font-normal text-gray-500">Used for inbound pickups and ASN source selection</div>
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('destination');
              resetForm();
            }}
            className={`rounded-md px-4 py-3 text-sm font-medium ${
              activeTab === 'destination' ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Destination Addresses
            <div className="mt-1 text-xs font-normal text-gray-500">Used for outbound shipping destinations</div>
          </button>
        </div>
      </div>

      {showForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingAddress ? 'Edit Address' : `Add ${activeTab === 'source' ? 'Source' : 'Destination'} Address`}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Name</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                <input
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
                <input
                  name="contact_phone"
                  value={formData.contact_phone}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input
                name="street_address"
                value={formData.street_address}
                onChange={handleInputChange}
                required
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
              <input
                name="address_line_2"
                value={formData.address_line_2}
                onChange={handleInputChange}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={(event) => {
                    const nextCountry = event.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      country: nextCountry,
                      state_province: ''
                    }));
                  }}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                >
                  {COUNTRIES.map((country) => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.country === 'Canada' ? 'Province' : 'State'}
                </label>
                {availableStates.length > 0 ? (
                  <select
                    name="state_province"
                    value={formData.state_province}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Select {formData.country === 'Canada' ? 'Province' : 'State'}</option>
                    {availableStates.map((state) => (
                      <option key={state.value} value={state.value}>{state.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    name="state_province"
                    value={formData.state_province}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.country === 'Canada' ? 'Postal Code' : 'ZIP Code'}
                </label>
                <input
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows="3"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>

            <div className="flex justify-end gap-3">
              {activeTab === 'destination' && (
                <label className="mr-auto inline-flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="is_default"
                    checked={formData.is_default}
                    onChange={handleInputChange}
                    className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Set as default
                </label>
              )}
              <button
                type="button"
                onClick={resetForm}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isLoading || updateMutation.isLoading}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {editingAddress ? 'Update Address' : 'Save Address'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-medium text-gray-900">
            {activeTab === 'source' ? 'Source Address Book' : 'Destination Address Book'}
          </h2>
        </div>

        {currentAddresses.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <FiMapPin className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-4 text-sm text-gray-500">No addresses found.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {currentAddresses.map((address) => (
              <div key={address.id} className="px-6 py-5 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-gray-900">{address.name}</h3>
                    {address.is_default && (
                      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-sm text-gray-600">
                    <div>{address.street_address}</div>
                    {address.address_line_2 && <div>{address.address_line_2}</div>}
                    <div>{[address.city, address.state_province, address.postal_code].filter(Boolean).join(', ')}</div>
                    <div>{address.country}</div>
                    {address.contact_name && <div className="mt-2">Contact: {address.contact_name}</div>}
                    {address.contact_phone && <div>Phone: {address.contact_phone}</div>}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(address)}
                    className="rounded-md p-2 text-indigo-600 hover:bg-indigo-50"
                    title="Edit"
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(address.id)}
                    className="rounded-md p-2 text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerAddresses;
